/* ═══════════════════════════════════════════════════
   TIPS CONTENT — Groups → Tips (collapsible) → Plays
   ═══════════════════════════════════════════════════ */

if (!customElements.get("tips-content-component")) {
  class TipsContentComponent extends HTMLElement {
    constructor() {
      super();
      this.data = null;
    }

    connectedCallback() {
      this.loadData();
    }

    /* ══════════════════════════════════════════════
       DATA
       ══════════════════════════════════════════════ */

    loadData() {
      const tag = this.querySelector("[data-tips-json]");
      if (!tag) {
        this._hideLoading();
        return;
      }
      try {
        this.data = JSON.parse(tag.textContent);
      } catch (err) {
        console.error("[tips-content] Failed to parse JSON:", err);
        this._hideLoading();
        return;
      }
      this._hideLoading();
      this.render();
      this.bindEvents();
    }

    _hideLoading() {
      const el = this.querySelector("[data-tips-loading]");
      if (el) el.style.display = "none";
    }

    /* ══════════════════════════════════════════════
       RENDER
       ══════════════════════════════════════════════ */

    render() {
      const container = this.querySelector("[data-tips-container]");
      if (!container) return;

      const groups = Array.isArray(this.data && this.data.groups) ? this.data.groups : [];
      if (groups.length === 0) {
        container.innerHTML = `<div class="tc-empty"><p>No tip groups found in JSON.</p></div>`;
        return;
      }

      container.innerHTML = groups.map((g, gi) => this._renderGroup(g, gi)).join("");
    }

    _renderGroup(group, gi) {
      const title = group.title || "";
      const tips = Array.isArray(group.tips) ? group.tips : [];
      return `
        <div class="tc-group" data-group-index="${gi}">
          ${title ? `<h3 class="tc-group-title">${this._esc(title)}</h3>` : ""}
          <div class="tc-tips">
            ${tips.map((tip, ti) => this._renderTip(tip, gi, ti)).join("")}
          </div>
        </div>`;
    }

    _renderTip(tip, gi, ti) {
      const title = tip.title || `Tip ${ti + 1}`;
      const text = tip.text || "";
      const hasImage = tip.image && tip.image.url;
      const hasVideo = tip.video && tip.video.url;
      const plays = Array.isArray(tip.plays) ? tip.plays : [];

      const chevron = `<svg class="tc-tip-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;

      let body = "";
      if (text.trim()) {
        body += `<p class="tc-tip-text">${this._esc(text)}</p>`;
      }
      if (hasImage) {
        body += `
          <div class="tc-tip-image">
            <img src="${this._esc(tip.image.url)}" alt="${this._esc(tip.image.alt || title)}" loading="lazy">
          </div>`;
      }
      if (hasVideo) {
        const embedClass = this._isVimeo(tip.video.url) ? " tc-tip-video--embed" : "";
        body += `
          <div class="tc-tip-video${embedClass}">
            ${this._videoInnerHTML(tip.video.url)}
          </div>`;
      }
      if (plays.length > 0) {
        const openByDefault = plays.length === 1;
        body += `
          <div class="tc-plays">
            ${plays.map((p) => this._renderPlay(p, openByDefault)).join("")}
          </div>`;
      }

      const bookIcon = `<svg class="tc-tip-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>`;

      return `
        <div class="tc-tip" data-tip-index="${gi}-${ti}">
          <button class="tc-tip-header" type="button" aria-expanded="false">
            <span class="tc-tip-title-wrap">
              ${bookIcon}
              <span class="tc-tip-title">${this._esc(title)}</span>
            </span>
            ${chevron}
          </button>
          <div class="tc-tip-body">${body}</div>
        </div>`;
    }

    _renderPlay(play, openByDefault = false) {
      const name = play.name || "Play";
      const options = Array.isArray(play.options) ? play.options : [];
      const chevron = `<svg class="tc-play-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;

      return `
        <div class="tc-play${openByDefault ? " open" : ""}" data-play-id="${this._esc(play.id || "")}">
          <button class="tc-play-header" type="button" aria-expanded="${openByDefault}">
            <span class="tc-play-name">${this._esc(name)}</span>
            ${chevron}
          </button>
          <div class="tc-play-body">
            ${this._renderOptionTabs(options)}
            ${options.map((opt, oi) => this._renderOption(opt, oi)).join("")}
          </div>
        </div>`;
    }

    _renderOptionTabs(options) {
      if (options.length <= 1) return "";
      const firstTitle = this._esc(options[0].title || "Option 1");
      return `
        <div class="tc-option-nav" data-option-nav data-total="${options.length}" data-current="0">
          <button class="tc-option-nav-arrow" data-nav-prev aria-label="Previous option">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <span class="tc-option-nav-label" data-nav-label>
            <span class="tc-option-nav-name">${firstTitle}</span>
            <span class="tc-option-nav-count">1 / ${options.length}</span>
          </span>
          <button class="tc-option-nav-arrow" data-nav-next aria-label="Next option">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>`;
    }

    _renderOption(opt, index) {
      const hasImage = opt.image && opt.image.url;
      const hasVideo = opt.video && opt.video.url;
      let mediaHTML = "";

      if (hasImage || hasVideo) {
        const imageBlock = hasImage
          ? `<div class="tc-option-media tc-option-media--image"><img src="${this._esc(opt.image.url)}" alt="${this._esc(opt.image.alt || opt.title || "")}" loading="lazy"></div>`
          : "";
        const videoEmbedClass = hasVideo && this._isVimeo(opt.video.url) ? " tc-option-media--embed" : "";
        const videoBlock = hasVideo
          ? `<div class="tc-option-media tc-option-media--video${videoEmbedClass}">${this._videoInnerHTML(opt.video.url)}</div>`
          : "";
        if (hasImage && hasVideo) {
          mediaHTML = `<div class="tc-option-media-grid">${imageBlock}${videoBlock}</div>`;
        } else {
          mediaHTML = imageBlock || videoBlock;
        }
      } else {
        mediaHTML = `<div class="tc-option-media-placeholder">No media</div>`;
      }

      const fields = [];
      if (opt.setup && String(opt.setup).trim()) {
        fields.push(`
          <div class="tc-option-field">
            <div class="tc-option-field-label">Setup</div>
            <div class="tc-option-field-text">${this._esc(opt.setup)}</div>
          </div>`);
      }
      if (opt.reads && String(opt.reads).trim()) {
        fields.push(`
          <div class="tc-option-field">
            <div class="tc-option-field-label">Reads</div>
            <div class="tc-option-field-text">${this._esc(opt.reads)}</div>
          </div>`);
      }

      return `
        <div class="tc-option${index === 0 ? " active" : ""}" data-option-index="${index}">
          <h5 class="tc-option-title">${this._esc(opt.title || `Option ${index + 1}`)}</h5>
          ${mediaHTML}
          ${fields.length ? `<div class="tc-option-fields">${fields.join("")}</div>` : ""}
        </div>`;
    }

    /* ══════════════════════════════════════════════
       BINDINGS
       ══════════════════════════════════════════════ */

    bindEvents() {
      /* Tip collapsibles */
      this.querySelectorAll(".tc-tip-header").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const wrap = btn.closest(".tc-tip");
          const isOpen = wrap.classList.contains("open");
          wrap.classList.toggle("open", !isOpen);
          btn.setAttribute("aria-expanded", String(!isOpen));
        });
      });

      /* Play collapsibles */
      this.querySelectorAll(".tc-play-header").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const wrap = btn.closest(".tc-play");
          const isOpen = wrap.classList.contains("open");
          wrap.classList.toggle("open", !isOpen);
          btn.setAttribute("aria-expanded", String(!isOpen));
        });
      });

      /* Option nav prev/next inside each play */
      this.querySelectorAll(".tc-play").forEach((play) => {
        const nav = play.querySelector("[data-option-nav]");
        if (!nav) return;
        const panels = play.querySelectorAll(".tc-option");
        this._bindOptionNav(nav, panels);
      });

      /* Image lightbox: tip image + option image */
      this.addEventListener("click", (e) => {
        const tipImg = e.target.closest(".tc-tip-image");
        const optImg = e.target.closest(".tc-option-media--image");
        const wrap = tipImg || optImg;
        if (!wrap) return;
        const img = wrap.querySelector("img");
        if (!img) return;
        this._openImageLightbox(img.currentSrc || img.src, img.alt);
      });
    }

    _bindOptionNav(nav, panels) {
      if (!nav || !panels || !panels.length) return;
      const total = parseInt(nav.dataset.total, 10);
      let current = 0;
      const label = nav.querySelector("[data-nav-label]");
      const prevBtn = nav.querySelector("[data-nav-prev]");
      const nextBtn = nav.querySelector("[data-nav-next]");
      const names = [];
      panels.forEach((p, i) => {
        const title = p.querySelector(".tc-option-title");
        names.push(title ? title.textContent : `Option ${i + 1}`);
      });

      const update = () => {
        panels.forEach((p, i) => p.classList.toggle("active", i === current));
        if (label) label.innerHTML = `<span class="tc-option-nav-name">${this._esc(names[current])}</span><span class="tc-option-nav-count">${current + 1} / ${total}</span>`;
        prevBtn.classList.toggle("disabled", current === 0);
        nextBtn.classList.toggle("disabled", current === total - 1);
        nav.dataset.current = String(current);
      };

      prevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current > 0) { current--; update(); }
      });
      nextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current < total - 1) { current++; update(); }
      });

      update();
    }

    /* ══════════════════════════════════════════════
       LIGHTBOX
       ══════════════════════════════════════════════ */

    _openImageLightbox(src, alt) {
      const overlay = document.createElement("div");
      overlay.className = "tc-lightbox-overlay";
      overlay.innerHTML = `
        <button class="tc-lightbox-close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <img src="${src}" alt="${alt || ''}">
      `;
      document.body.appendChild(overlay);
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => overlay.classList.add("open"));

      const close = () => {
        overlay.classList.remove("open");
        document.body.style.overflow = "";
        setTimeout(() => overlay.remove(), 300);
      };

      overlay.querySelector(".tc-lightbox-close").addEventListener("click", (e) => {
        e.stopPropagation();
        close();
      });
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
      });

      const escHandler = (e) => {
        if (e.key === "Escape") {
          close();
          document.removeEventListener("keydown", escHandler);
        }
      };
      document.addEventListener("keydown", escHandler);
    }

    /* ══════════════════════════════════════════════
       HELPERS
       ══════════════════════════════════════════════ */

    _esc(str) {
      const d = document.createElement("div");
      d.textContent = str || "";
      return d.innerHTML;
    }

    _parseVimeoEmbed(url) {
      if (!url) return null;
      const m = url.match(/(?:vimeo\.com\/(?:video\/|manage\/videos\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)(?:[\/?#]?h=([a-zA-Z0-9]+)|\/([a-zA-Z0-9]+))?/);
      if (!m) return null;
      const id = m[1];
      const hash = m[2] || m[3];
      return hash
        ? `https://player.vimeo.com/video/${id}?h=${hash}`
        : `https://player.vimeo.com/video/${id}`;
    }

    _videoInnerHTML(url) {
      const embed = this._parseVimeoEmbed(url);
      if (embed) {
        return `<iframe src="${embed}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
      }
      return `<video src="${this._esc(url)}" controls playsinline></video>`;
    }

    _isVimeo(url) {
      return !!this._parseVimeoEmbed(url);
    }

    /* ══════════════════════════════════════════════
       SHOPIFY EDITOR RESET
       ══════════════════════════════════════════════ */

    reset() {
      this.loadData();
    }
  }

  customElements.define("tips-content-component", TipsContentComponent);
}

/* ── Shopify editor: re-initialize on section re-render ── */
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target;
    const component = section.querySelector("tips-content-component");
    if (component) component.reset();
  });
}
